import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHotelStore } from "@store/hotelStore";
import { useAuthStore } from "@store/authStore";
import { useLocaleStore, LOCALE_OPTIONS } from "@store/localeStore";
import { getHotels, createMyHotel } from "@/api/hotels";

export function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hotels, selectedHotelId, setSelectedHotel, setHotels } = useHotelStore();
  const { user, logout } = useAuthStore();
  const { locale, setLocale } = useLocaleStore();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  // New hotel popover state
  const [newHotelOpen, setNewHotelOpen] = useState(false);
  const [newHotelName, setNewHotelName] = useState("");
  const [creating, setCreating] = useState(false);
  const newHotelRef = useRef<HTMLDivElement>(null);

  const canCreateHotel = user?.is_superadmin || user?.org_role === "admin";

  // Load hotels on mount if the store is empty (e.g. after a page refresh)
  useEffect(() => {
    if (hotels.length === 0) {
      getHotels().then(setHotels).catch(() => {});
    }
  }, [hotels.length, setHotels]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (newHotelRef.current && !newHotelRef.current.contains(e.target as Node)) {
        setNewHotelOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  async function handleCreateHotel() {
    if (!newHotelName.trim() || creating) return;
    setCreating(true);
    try {
      const hotel = await createMyHotel(newHotelName.trim());
      const updated = await getHotels();
      setHotels(updated);
      setSelectedHotel(hotel.id);
      setNewHotelOpen(false);
      setNewHotelName("");
      navigate("/my-hotel/general");
    } catch {
      // Error handling — API interceptor will show toast if configured
    } finally {
      setCreating(false);
    }
  }

  const currentLabel = LOCALE_OPTIONS.find((o) => o.code === locale)?.label ?? locale;

  return (
    <header className="flex h-16 items-center justify-between border-b border-dark-border bg-dark-card px-6">
      {/* Hotel selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-400">{t("header.hotel")}</label>
        <select
          value={selectedHotelId ?? ""}
          onChange={(e) => setSelectedHotel(e.target.value)}
          className="rounded-md border border-dark-border bg-dark-bg px-3 py-1.5 text-sm text-gray-200 focus:border-accent-blue focus:outline-none"
        >
          {hotels.length === 0 && (
            <option value="" disabled>{t("header.loading")}</option>
          )}
          {hotels.map((hotel) => (
            <option key={hotel.id} value={hotel.id}>{hotel.name}</option>
          ))}
        </select>

        {/* New hotel button */}
        {canCreateHotel && (
          <div ref={newHotelRef} className="relative">
            <button
              onClick={() => setNewHotelOpen(!newHotelOpen)}
              title={t("header.newHotel")}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-dark-border bg-dark-bg text-gray-400 hover:bg-dark-hover hover:text-white transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </button>
            {newHotelOpen && (
              <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-lg border border-dark-border bg-dark-card p-4 shadow-xl">
                <label className="mb-1.5 block text-xs font-medium text-gray-400">
                  {t("header.hotelName")}
                </label>
                <input
                  type="text"
                  value={newHotelName}
                  onChange={(e) => setNewHotelName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateHotel()}
                  placeholder={t("header.hotelNamePlaceholder")}
                  autoFocus
                  className="mb-3 w-full rounded-md border border-dark-border bg-dark-bg px-3 py-1.5 text-sm text-gray-200 placeholder-gray-500 focus:border-accent-blue focus:outline-none"
                />
                <button
                  onClick={handleCreateHotel}
                  disabled={!newHotelName.trim() || creating}
                  className="w-full rounded-md bg-accent-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-blue/90 disabled:opacity-50 transition-colors"
                >
                  {t("header.create")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User menu + language */}
      <div className="flex items-center gap-4">
        {/* Language selector */}
        <div ref={langRef} className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-gray-400 hover:bg-dark-hover hover:text-white transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802" />
            </svg>
            {currentLabel}
          </button>
          {langOpen && (
            <div className="absolute right-0 top-full z-50 mt-1 w-44 rounded-lg border border-dark-border bg-dark-card py-1 shadow-xl">
              {LOCALE_OPTIONS.map((opt) => (
                <button
                  key={opt.code}
                  onClick={() => { setLocale(opt.code); setLangOpen(false); }}
                  className={`block w-full px-4 py-2 text-left text-sm transition-colors ${
                    opt.code === locale
                      ? "bg-accent-blue/20 text-accent-blue"
                      : "text-gray-300 hover:bg-dark-hover hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className="text-sm text-gray-400">
          {user?.first_name} {user?.last_name}
        </span>
        <button
          onClick={logout}
          className="rounded-md px-3 py-1.5 text-sm text-gray-400 hover:bg-dark-hover hover:text-white transition-colors"
        >
          {t("header.signOut")}
        </button>
      </div>
    </header>
  );
}
